import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button, Table, Form, InputGroup, Modal, Card } from "react-bootstrap";
import { genericGetAll } from "../generic/crud";
import { ENTITY_ID_LEDGER_ACCOUNT } from "../generic/Constants";

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

export function SalePaymentsSection({ payments, onChange, readOnly = false, balanceDueInfo }) {
    const [ledgerAccounts, setLedgerAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [ledgerSearchText, setLedgerSearchText] = useState("");

    const [newPayment, setNewPayment] = useState({
        referenceNumber: '',
        narration: '',
        totalAmount: '',
        ledgerAccountId: null,
        ledgerAccountName: '',
        ledgerType: '',
    });
    const amountInputRef = useRef(null);

    // Load ledger accounts on mount
    useEffect(() => {
        setLoading(true);
        genericGetAll(ENTITY_ID_LEDGER_ACCOUNT, { pageNumber: 1, pageSize: 1000 })
            .then((response) => {
                const responseData = response?.data || response;
                const accounts = responseData?.entities || responseData?.data || [];
                setLedgerAccounts(Array.isArray(accounts) ? accounts : []);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error loading ledger accounts:", err);
                setLedgerAccounts([]);
                setLoading(false);
            });
    }, []);

    // Filter ledger accounts based on search
    const filteredLedgerAccounts = ledgerAccounts.filter(account =>
        !account.systemReserved &&
        (account.ledgerName?.toLowerCase().includes(ledgerSearchText.toLowerCase()) ||
        account.ledgerType?.toLowerCase().includes(ledgerSearchText.toLowerCase()))
    );

    // Handle ledger selection from modal - this adds the payment directly
    const handleLedgerSelect = useCallback((ledger) => {
        setNewPayment({
            referenceNumber: '',
            narration: '',
            totalAmount: '',
            ledgerAccountId: ledger.id,
            ledgerAccountName: ledger.ledgerName,
            ledgerType: ledger.ledgerType,
        });
        setShowLedgerModal(false);
        setLedgerSearchText("");
        setShowPaymentModal(true);
        // Focus on amount input after modal opens
        setTimeout(() => {
            if (amountInputRef.current) {
                amountInputRef.current.focus();
            }
        }, 200);
    }, []);

    // Open add payment flow - first select ledger
    const handleOpenAddPayment = () => {
        setShowLedgerModal(true);
    };

    // Add new payment
    const handleAddPayment = useCallback(() => {
        if (!newPayment.totalAmount || parseFloat(newPayment.totalAmount) <= 0) {
            return;
        }

        if (!newPayment.ledgerAccountId) {
            return;
        }

        const paymentToAdd = {
            referenceNumber: newPayment.referenceNumber,
            narration: newPayment.narration,
            totalAmount: parseFloat(newPayment.totalAmount),
            ledgerAccountId: newPayment.ledgerAccountId,
            ledgerAccountName: newPayment.ledgerAccountName,
            ledgerType: newPayment.ledgerType,
        };

        onChange([...payments, paymentToAdd]);

        // Reset form and close modal
        setNewPayment({
            referenceNumber: '',
            narration: '',
            totalAmount: '',
            ledgerAccountId: null,
            ledgerAccountName: '',
            ledgerType: '',
        });
        setShowPaymentModal(false);
    }, [payments, newPayment, onChange]);

    // Remove payment
    const handleRemovePayment = useCallback((index) => {
        const updatedPayments = payments.filter((_, i) => i !== index);
        onChange(updatedPayments);
    }, [payments, onChange]);

    // Update payment amount
    const handleUpdateAmount = useCallback((index, value) => {
        const updatedPayments = [...payments];
        updatedPayments[index].totalAmount = parseFloat(value) || 0;
        onChange(updatedPayments);
    }, [payments, onChange]);

    // Handle Enter key to add payment quickly
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && newPayment.totalAmount && newPayment.ledgerAccountId) {
            e.preventDefault();
            handleAddPayment();
        }
    };

    // Calculate total payments
    const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);

    // Format ledger type for display
    const formatLedgerType = (type) => {
        if (!type) return "-";
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).replace(/\B\w+/g, l => l.toLowerCase());
    };

    return (
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
            <div className="d-flex justify-content-between align-items-center px-4 py-2" style={{ backgroundColor: '#e8f4fc', borderRadius: '8px 8px 0 0' }}>
                <div className="d-flex align-items-center">
                    <i className="bi bi-credit-card me-2" style={{ color: '#4a90a4' }}></i>
                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>PAYMENTS</span>
                </div>
                {!readOnly && (
                    <Button
                        variant="light"
                        size="sm"
                        className="text-primary"
                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa' }}
                        onClick={handleOpenAddPayment}
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Payment
                    </Button>
                )}
            </div>
            <Card.Body className="p-0">
                {/* Payments Table */}
                {payments.length > 0 ? (
                    <>
                        <Table hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-3" style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '25%' }}>Ledger Account</th>
                                    <th style={{ width: '20%' }}>Amount</th>
                                    <th style={{ width: '15%' }}>Reference</th>
                                    <th style={{ width: '20%' }}>Narration</th>
                                    {!readOnly && <th style={{ width: '15%' }} className="text-center">Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment, index) => (
                                    <tr key={index}>
                                        <td className="text-muted ps-3">{index + 1}</td>
                                        <td>
                                            <div>
                                                <strong>{payment.ledgerAccountName || '-'}</strong>
                                                {payment.ledgerType && (
                                                    <small className="text-muted d-block">
                                                        {formatLedgerType(payment.ledgerType)}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {readOnly ? (
                                                <span className="text-success fw-semibold">
                                                    {formatCurrency(payment.totalAmount)}
                                                </span>
                                            ) : (
                                                <InputGroup size="sm" style={{ width: '130px' }}>
                                                    <InputGroup.Text>₹</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        value={payment.totalAmount}
                                                        onChange={(e) => handleUpdateAmount(index, e.target.value)}
                                                    />
                                                </InputGroup>
                                            )}
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {payment.referenceNumber || '-'}
                                            </small>
                                        </td>
                                        <td>
                                            <small>{payment.narration || '-'}</small>
                                        </td>
                                        {!readOnly && (
                                            <td className="text-center">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemovePayment(index)}
                                                    title="Remove payment"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-secondary">
                                <tr>
                                    <td colSpan={2} className="text-end fw-bold ps-3">Total Payments:</td>
                                    <td colSpan={4}>
                                        <strong className="text-success fs-6">
                                            {formatCurrency(totalPayments)}
                                        </strong>
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </>
                ) : (
                    <div className="text-center py-4 text-muted">
                        <i className="bi bi-wallet fs-1 d-block mb-2 opacity-50"></i>
                        <p className="mb-0">No payments added yet</p>
                        <small>Click "Add Payment" button to add payment entries</small>
                    </div>
                )}
            </Card.Body>

            {/* Step 1: Select Ledger Account Modal */}
            <Modal show={showLedgerModal} onHide={() => { setShowLedgerModal(false); setLedgerSearchText(""); }} centered>
                <Modal.Header closeButton className="bg-warning bg-opacity-10">
                    <Modal.Title className="text-warning">
                        <i className="bi bi-book me-2"></i>
                        Select Ledger Account
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {/* Search Field */}
                    <div className="p-3 border-bottom">
                        <Form.Control
                            type="text"
                            placeholder="Search ledger accounts..."
                            value={ledgerSearchText}
                            onChange={(e) => setLedgerSearchText(e.target.value)}
                            autoFocus
                        />
                        <small className="text-muted">Click on a ledger to select it</small>
                    </div>

                    {/* Ledger Accounts Table */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-warning me-2" role="status"></div>
                                Loading ledger accounts...
                            </div>
                        ) : (
                            <Table hover className="mb-0">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th className="ps-3">Ledger Name</th>
                                        <th className="text-end pe-3" style={{ width: '150px' }}>Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLedgerAccounts.length > 0 ? (
                                        filteredLedgerAccounts.map((account) => (
                                            <tr
                                                key={account.id}
                                                onClick={() => handleLedgerSelect(account)}
                                                style={{ cursor: 'pointer' }}
                                                className="ledger-item-row"
                                            >
                                                <td className="ps-3 py-3">
                                                    <span className="fw-semibold text-primary">{account.ledgerName}</span>
                                                </td>
                                                <td className="text-end pe-3 py-3">
                                                    <span className="badge bg-secondary">
                                                        {formatLedgerType(account.ledgerType)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center py-4 text-muted">
                                                {ledgerSearchText ? 'No ledgers match your search' : 'No ledger accounts available'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="text-secondary" style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }} onClick={() => { setShowLedgerModal(false); setLedgerSearchText(""); }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Step 2: Payment Details Modal */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
                <Modal.Header closeButton className="bg-warning bg-opacity-10">
                    <Modal.Title className="text-warning">
                        <i className="bi bi-credit-card me-2"></i>
                        Add Payment Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Selected Ledger Display */}
                    <div className="mb-3 p-3 bg-light rounded border">
                        <small className="text-muted d-block">Selected Ledger Account</small>
                        <strong className="text-primary">{newPayment.ledgerAccountName}</strong>
                        {newPayment.ledgerType && (
                            <span className="badge bg-secondary ms-2">
                                {formatLedgerType(newPayment.ledgerType)}
                            </span>
                        )}
                    </div>

                    {/* Amount to be Collected */}
                    {balanceDueInfo && (
                        <div className="mb-3 p-3 rounded border" style={{ backgroundColor: '#fff8e1' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted d-block">Grand Total</small>
                                    <span className="fw-semibold">{formatCurrency(balanceDueInfo.grandTotal)}</span>
                                </div>
                                <div className="text-end">
                                    <small className="text-muted d-block">Balance Due</small>
                                    <span className={`fw-bold fs-5 ${balanceDueInfo.balanceDue > 0 ? 'text-danger' : 'text-success'}`}>
                                        {formatCurrency(balanceDueInfo.balanceDue)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Amount <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control
                                ref={amountInputRef}
                                type="number"
                                step="0.01"
                                placeholder="Enter amount"
                                value={newPayment.totalAmount}
                                onChange={(e) => setNewPayment({ ...newPayment, totalAmount: e.target.value })}
                                onKeyDown={handleKeyDown}
                            />
                        </InputGroup>
                    </Form.Group>

                    <div className="row g-3">
                        <div className="col-6">
                            <Form.Group>
                                <Form.Label className="fw-semibold">Reference #</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ref No."
                                    value={newPayment.referenceNumber}
                                    onChange={(e) => setNewPayment({ ...newPayment, referenceNumber: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-6">
                            <Form.Group>
                                <Form.Label className="fw-semibold">Narration</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Description"
                                    value={newPayment.narration}
                                    onChange={(e) => setNewPayment({ ...newPayment, narration: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="text-secondary" style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }} onClick={() => setShowPaymentModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="light"
                        className="text-primary"
                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa' }}
                        onClick={handleAddPayment}
                        disabled={!newPayment.totalAmount || parseFloat(newPayment.totalAmount) <= 0}
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Payment
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}
